import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';

function normArr(data) {
  return Array.isArray(data) ? data : [];
}

function pickId(row) {
  if (!row || typeof row !== 'object') return null;
  const v = row.id ?? row.competition_id ?? row.season_id ?? row.box_id ?? row.boxLevelId;
  return v == null || v === '' ? null : String(v);
}

async function list(path) {
  const { data } = await v2Api.get(path);
  return normArr(data);
}

async function post(path, payload) {
  const { data } = await v2Api.post(path, payload);
  return data;
}

export const catalogService = {
  competitions: {
    list: () => list('/Competition/list'),
    create: (payload) => post('/Competition/create', payload),
    update: (payload) => post('/Competition/update', payload),
    remove: (id) => post('/Competition/delete', { id }),
    id: pickId,
  },
  seasons: {
    list: () => list('/Season/admin_list'),
    create: (payload) => post('/Season/create', payload),
    update: (payload) => post('/Season/update', payload),
    remove: (id) => post('/Season/delete', { id }),
    /** Image (bannière) de la saison : héro « Box league » de l'app. Multipart, comme la photo d'un tournoi. */
    uploadImage: async (id, file) => {
      const fd = new FormData();
      fd.append('id', id);
      fd.append('file', file);
      const access = await ensureAccessToken(API_ROOT);
      const headers = clubRequestHeaders();
      if (access) headers.Authorization = `Bearer ${access}`;
      const { data } = await axios.post(`${API_ROOT}/Season/upload_image`, fd, {
        headers,
        maxContentLength: 12 * 1024 * 1024,
        maxBodyLength: 12 * 1024 * 1024,
      });
      return data;
    },
    clearImage: (id) => post('/Season/update', { id, image_url: '' }),
    setRunning: (id) => post('/Season/set_running', { id }),
    /** Réglages de box d'une saison : `{ season_id, settings, effective }` (effective = repli AppSetting résolu). */
    /** Pré-remplissage d'une nouvelle saison (copie de la dernière saison de la compétition). */
    creationDefaults: async (competitionId) => {
      const params = competitionId ? { competition_id: String(competitionId) } : {};
      const { data } = await v2Api.get('/Season/creation_defaults', { params });
      return data;
    },
    boxSettings: async (seasonId) => {
      const { data } = await v2Api.get('/Season/box_settings', { params: { season_id: String(seasonId) } });
      return data;
    },
    /**
     * Barème de box par issue (lot 7) : `{ outcomes: [{ units_won, units_lost, result, generated_points,
     * sport_points, season_points, effective_points }], generated_not_played, season_not_played,
     * effective_not_played, scoring_format_name }`. `season_id`, ou `sport_id` + `scoring_format_id` avant création
     * (`scoring_format_id` avec `season_id` = aperçu d'un format pas encore enregistré).
     */
    pointsRulesPreview: async ({ season_id, sport_id, scoring_format_id } = {}) => {
      const params = {};
      if (season_id) params.season_id = String(season_id);
      if (sport_id) params.sport_id = String(sport_id);
      if (scoring_format_id) params.scoring_format_id = String(scoring_format_id);
      const { data } = await v2Api.get('/Season/points_rules_preview', { params });
      return data;
    },
    /** Remplace le barème de la saison (`rules` vide + `not_played` null = retour au barème généré) ; renvoie l'aperçu. */
    updatePointsRules: ({ season_id, rules, not_played }) =>
      post('/Season/update_points_rules', { season_id: String(season_id), rules: rules || [], not_played: not_played ?? null }),
    id: pickId,
  },
  boxes: {
    list: () => list('/Box/list'),
    create: (payload) => post('/Box/create', payload),
    update: (payload) => post('/Box/update', payload),
    remove: (id) => post('/Box/delete', { id }),
    id: pickId,
  },
  boxMemberships: {
    listByBox: (box_id, include_inactive = false) => post('/BoxMembership/list', { box_id, include_inactive }),
    create: (payload) => post('/BoxMembership/create', payload),
    update: (payload) => post('/BoxMembership/update', payload),
    remove: (id) => post('/BoxMembership/delete', { id }),
    id: pickId,
  },
};

